import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaArrowRight } from "react-icons/fa6"; // Assuming you are using react-icons

gsap.registerPlugin(ScrollTrigger);

const ProgramSection = () => {
    const sectionRef = useRef(null);
    // Image section refs
    const imageContainerRef = useRef(null);
    const overlayRef = useRef(null);
    const imgRef = useRef(null);
    // Text section refs
    const textContentRefs = useRef([]); // For h3, h5, p, h6
    const listItemsRefs = useRef([]); // For the li elements


    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%", // Start animation when top of section is 75% down the viewport
                    toggleActions: "play none none none", // Play once
                }
            });

            // --- 1. Image Reveal Animation ---
            // We set the transform origin to the left so it wipes away to the right
            gsap.set(overlayRef.current, { transformOrigin: "left center" });

            tl.to(overlayRef.current, {
                scaleX: 0, // Shrink width to 0
                duration: 1,
                ease: "power3.inOut"
            })
            // Simultaneously, give the image a slight zoom-out effect for depth
            .from(imgRef.current, {
                scale: 1.1,
                duration: 1.2,
                ease: "power2.out"
            }, "<"); // "<" starts at the same time as the overlay animation starts


            // --- 2. Text Content Cascade ---
            // Animate H3, H5, P, H6
            tl.from(textContentRefs.current, {
                y: 40,
                opacity: 0,
                duration: 0.6,
                stagger: 0.15,
                ease: "power2.out"
            }, "-=0.8"); // Start this before the image animation finishes


            // --- 3. List Items Cascade ---
            // Animate list items slightly differently (maybe slide from right slightly)
            tl.from(listItemsRefs.current, {
                x: 20, // Come in from the right slightly
                y: 20, // And up
                opacity: 0,
                duration: 0.5,
                stagger: 0.1, // Faster stagger for list items
                ease: "back.out(1.5)" // Slight bouncy arrival
            }, "-=0.1");

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Helpers to manage ref arrays
    const addToTextRefs = (el) => { if (el && !textContentRefs.current.includes(el)) textContentRefs.current.push(el); };
    const addToListRefs = (el) => { if (el && !listItemsRefs.current.includes(el)) listItemsRefs.current.push(el); };


    return (
        <section ref={sectionRef} className='container max-w-7xl mx-auto px-6 md:px-12 my-[100px]'>
            <article className='grid md:grid-cols-2 gap-8'>
                {/* Left Column: Image with Reveal Effect */}
                {/* Added relative, h-full and overflow-hidden to contain the absolute overlay */}
                <div ref={imageContainerRef} className="relative h-full w-full rounded-[20px] overflow-hidden">
                    {/* The Yellow Curtain Overlay */}
                    <div
                        ref={overlayRef}
                        className="absolute inset-0 bg-[#EAB308] z-10"
                    ></div>
                    {/* The Image */}
                    <img
                        ref={imgRef}
                        src='img/program-timeline.png'
                        alt="Program Timeline"
                        className='w-full h-full object-cover'
                    />
                </div>

                {/* Right Column: Text Content */}
                <div className='flex flex-col py-3 gap-y-6 h-full justify-between'>
                    <h3 ref={addToTextRefs} className='text-[#000000] font-[600] text-[34px] sm:text-[40px] leading-[48px]'>Program Timeline</h3>
                    <ol className='flex flex-col gap-y-6'>
                        {/* I'm assuming these list items might be dynamic later, but for now applied refs manually to the static list */}
                        <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                            <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                            <p className='text-[#374151] text-[15px] font-[600]'>Week 0: Selection & Onboarding</p>
                        </li>
                        <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                            <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                            <p className='text-[#374151] text-[15px] font-[600]'>Week 1–4: Product + GTM Foundation</p>
                        </li>
                        <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                            <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                            <p className='text-[#374151] text-[15px] font-[600]'>Week 5–8: Traction + Experiments</p>
                        </li>
                        <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                            <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                            <p className='text-[#374151] text-[15px] font-[600]'>Week 9–12: Fundraising + Demo Day</p>
                        </li>
                    </ol>
                    <a href='https://forms.gle/2F4A8ZQeJ4XTmVMa8' target='_blank' ref={addToTextRefs} className='self-start rounded-full bg-[#0D3BD1] hover:bg-blue-500 px-5 py-2 text-sm text-white font-[500] leading-[20px]'>Apply Now</a>
                </div>
            </article>
        </section>
    );
};

export default ProgramSection;